import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Delete, Edit, Add, CloudUpload } from '@mui/icons-material';
import API_BASE_URL from '../apiConfig';
import { useTheme as useCustomTheme } from '../ThemeContext';
import { getDataGridStyles } from '../styles/dataGridStyles';

function CarouselPictureManager({ clubId, clubHandle }) {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const dataGridStyles = getDataGridStyles(darkMode);
  
  // Define DataGrid columns for carousel pictures
  const carouselColumns = [
    {
      field: 'preview',
      headerName: 'Preview',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <img
          src={params.row.image_url}
          alt={params.row.caption || 'Carousel picture'}
          style={{
            width: 80,
            height: 60,
            objectFit: 'cover',
            borderRadius: 4
          }}
        />
      )
    },
    {
      field: 'caption',
      headerName: 'Caption',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.caption || 'No caption'}
        </Typography>
      )
    },
    {
      field: 'upload_date',
      headerName: 'Upload Date',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(params.row.created_at).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'order',
      headerName: 'Order',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.display_order}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{ mr: 1 }}
          >
            <Edit />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <Delete />
          </IconButton>
        </Box>
      )
    }
  ];
  
  const [pictures, setPictures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [editingPicture, setEditingPicture] = useState(null);

  useEffect(() => {
    if (clubId) {
      fetchPictures();
    }
  }, [clubId]);

  const fetchPictures = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/carousel-pictures`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch carousel pictures');
      }
      
      const data = await response.json();
      setPictures(data);
    } catch (error) {
      console.error('Error fetching carousel pictures:', error);
      setError('Failed to load carousel pictures');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('carousel_picture', selectedFile);
      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }

      const response = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/carousel-pictures`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload picture');
      }

      const newPicture = await response.json();
      setPictures(prev => [...prev, newPicture]);
      setSuccess('Picture uploaded successfully!');
      setShowUploadDialog(false);
      setSelectedFile(null);
      setCaption('');
    } catch (error) {
      console.error('Error uploading picture:', error);
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (picture) => {
    setEditingPicture(picture);
    setCaption(picture.caption || '');
    setShowEditDialog(true);
  };

  const handleUpdateCaption = async () => {
    try {
      setUploading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/carousel-pictures/${editingPicture.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ caption: caption.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update caption');
      }

      const updatedPicture = await response.json();
      setPictures(prev => prev.map(p => p.id === updatedPicture.id ? updatedPicture : p));
      setSuccess('Caption updated successfully!');
      setShowEditDialog(false);
      setEditingPicture(null);
      setCaption('');
    } catch (error) {
      console.error('Error updating caption:', error);
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (pictureId) => {
    if (!window.confirm('Are you sure you want to delete this picture?')) return;

    try {
      setError('');
      const response = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/carousel-pictures/${pictureId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete picture');
      }

      setPictures(prev => prev.filter(p => p.id !== pictureId));
      setSuccess('Picture deleted successfully!');
    } catch (error) {
      console.error('Error deleting picture:', error);
      setError(error.message);
    }
  };

  const resetDialogs = () => {
    setShowUploadDialog(false);
    setShowEditDialog(false);
    setSelectedFile(null);
    setCaption('');
    setEditingPicture(null);
    setError('');
  };

  if (loading) {
    return (
      <Card sx={{ mt: 4 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#e91e63' }}>
              Carousel Pictures
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowUploadDialog(true)}
              sx={{ 
                backgroundColor: '#e91e63', 
                color: '#ffffff',
                '&:hover': { 
                  backgroundColor: '#c2185b' 
                } 
              }}
            >
              Add Picture
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {pictures.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No carousel pictures uploaded yet. Click "Add Picture" to get started.
            </Typography>
          ) : (
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={pictures}
                columns={carouselColumns}
                pageSize={5}
                rowsPerPageOptions={[5, 10, 25]}
                disableSelectionOnClick
                sx={{
                  ...dataGridStyles,
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onClose={resetDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Carousel Picture</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="carousel-picture-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="carousel-picture-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                fullWidth
                sx={{ 
                  mb: 2,
                  color: darkMode ? '#ffffff' : '#000000',
                  borderColor: darkMode ? '#ffffff' : '#000000',
                  '&:hover': {
                    borderColor: darkMode ? '#ffffff' : '#000000',
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Choose Image File
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Caption (optional)"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Enter a caption for this picture..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialogs}>Cancel</Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            variant="contained"
            sx={{ 
              backgroundColor: '#e91e63', 
              color: '#ffffff',
              '&:hover': { 
                backgroundColor: '#c2185b' 
              } 
            }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onClose={resetDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Picture Caption</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {editingPicture && (
              <img
                src={editingPicture.image_url}
                alt={editingPicture.caption || 'Carousel picture'}
                style={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 8,
                  marginBottom: 16
                }}
              />
            )}
            <TextField
              fullWidth
              label="Caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Enter a caption for this picture..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialogs}>Cancel</Button>
          <Button
            onClick={handleUpdateCaption}
            disabled={uploading}
            variant="contained"
            sx={{ backgroundColor: '#e91e63', '&:hover': { backgroundColor: '#c2185b' } }}
          >
            {uploading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CarouselPictureManager; 